/**
 * Registre des modules.
 *
 * Chaque manifeste est importé explicitement : ajouter un module, c'est créer
 * son manifeste puis l'inscrire ici. Le registre est validé à l'import ; une
 * incohérence fait échouer le démarrage plutôt que de servir un état faux.
 */
import noyau from './manifestes/noyau.js';
import exercice from './manifestes/exercice.js';
import previsionnel from './manifestes/previsionnel.js';
import personnel from './manifestes/personnel.js';
import adherents from './manifestes/adherents.js';
import tresorerieVeille from './manifestes/tresorerie-veille.js';
import classement from './manifestes/classement.js';
import justificatifs from './manifestes/justificatifs.js';
import { LONGUEUR_MAX_TEXTE } from './reglages.js';
export const MANIFESTES = [noyau, exercice, previsionnel, personnel, adherents, tresorerieVeille, classement, justificatifs];
const KEBAB = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;
/** Décode un segment encodé en pourcentage ; un encodage invalide reste tel quel. */
function decoder(segment) {
    try {
        return decodeURIComponent(segment);
    }
    catch {
        return segment;
    }
}
/**
 * Segments d'un chemin, hors chaîne de requête. Le découpage précède le
 * décodage : `%2F` ne fabrique pas un segment de plus.
 */
function segments(chemin) {
    return (chemin.split('?')[0] ?? '')
        .split('/')
        .filter((s) => s !== '')
        .map(decoder);
}
/** Motif de refus d'une définition de réglage, ou `null`. */
function incoherenceReglage(d) {
    switch (d.type) {
        case 'nombre':
            if (!Number.isFinite(d.defaut))
                return 'défaut non fini';
            if (d.min !== undefined && !Number.isFinite(d.min))
                return 'borne min non finie';
            if (d.max !== undefined && !Number.isFinite(d.max))
                return 'borne max non finie';
            if (d.min !== undefined && d.max !== undefined && d.min > d.max)
                return 'min supérieur à max';
            if (d.min !== undefined && d.defaut < d.min)
                return 'défaut inférieur à min';
            if (d.max !== undefined && d.defaut > d.max)
                return 'défaut supérieur à max';
            return null;
        case 'choix':
            return Object.hasOwn(d.options, d.defaut) ? null : 'défaut absent des options';
        case 'texte':
            return d.defaut.length > LONGUEUR_MAX_TEXTE ? `défaut de plus de ${String(LONGUEUR_MAX_TEXTE)} caractères` : null;
        case 'booleen':
            return null;
    }
}
/** Lève une erreur décrivant la première incohérence trouvée. */
export function validerRegistre(manifestes) {
    const ids = new Set();
    for (const m of manifestes) {
        if (!KEBAB.test(m.id))
            throw new Error(`module « ${m.id} » : identifiant hors kebab-case`);
        if (ids.has(m.id))
            throw new Error(`module « ${m.id} » : identifiant dupliqué`);
        ids.add(m.id);
    }
    if (!ids.has('noyau'))
        throw new Error('le module « noyau » est absent du registre');
    const parId = new Map(manifestes.map((m) => [m.id, m]));
    for (const m of manifestes) {
        for (const d of m.dependances) {
            if (!parId.has(d))
                throw new Error(`module « ${m.id} » : dépendance inconnue « ${d} »`);
        }
        if (!m.desactivable) {
            for (const d of m.dependances) {
                if (parId.get(d)?.desactivable) {
                    throw new Error(`module « ${m.id} » : un module du noyau ne peut dépendre de « ${d} », désactivable`);
                }
            }
        }
    }
    // Cycles : parcours en profondeur avec pile.
    const etat = new Map();
    const visiter = (id, pile) => {
        const e = etat.get(id);
        if (e === 'fini')
            return;
        if (e === 'encours')
            throw new Error(`cycle de dépendances : ${[...pile, id].join(' → ')}`);
        etat.set(id, 'encours');
        for (const d of parId.get(id)?.dependances ?? [])
            visiter(d, [...pile, id]);
        etat.set(id, 'fini');
    };
    for (const m of manifestes)
        visiter(m.id, []);
    const routes = new Map();
    for (const m of manifestes) {
        for (const r of m.routesApi) {
            const s = segments(r);
            if (s[0] !== 'api')
                throw new Error(`module « ${m.id} » : la route « ${r} » doit commencer par /api`);
            if (s.length < 2)
                throw new Error(`module « ${m.id} » : la route « ${r} » doit avoir au moins deux segments`);
            const cle = s.join('/');
            const deja = routes.get(cle);
            if (deja)
                throw new Error(`route « ${r} » revendiquée par « ${deja} » et « ${m.id} »`);
            routes.set(cle, m.id);
        }
    }
    const droits = new Set();
    for (const m of manifestes) {
        for (const cle of Object.keys(m.droits)) {
            if (!cle.startsWith(`${m.id}.`))
                throw new Error(`module « ${m.id} » : droit « ${cle} » non préfixé par l'id`);
            if (!/^[a-z][a-z0-9-]*$/.test(cle.slice(m.id.length + 1))) {
                throw new Error(`module « ${m.id} » : droit « ${cle} » invalide, forme attendue « ${m.id}.<action> » en kebab-case`);
            }
            droits.add(cle);
        }
    }
    for (const m of manifestes) {
        for (const [cle, d] of Object.entries(m.reglages ?? {})) {
            const motif = incoherenceReglage(d);
            if (motif !== null)
                throw new Error(`module « ${m.id} » : réglage « ${cle} » incohérent (${motif})`);
        }
    }
    const vues = new Set();
    for (const m of manifestes) {
        for (const e of m.navigation) {
            if (vues.has(e.cle))
                throw new Error(`module « ${m.id} » : clé de navigation « ${e.cle} » dupliquée`);
            vues.add(e.cle);
            if (!droits.has(e.droit))
                throw new Error(`module « ${m.id} » : entrée « ${e.cle} » sur un droit inconnu « ${e.droit} »`);
        }
    }
}
validerRegistre(MANIFESTES);
export function getModule(id, manifestes = MANIFESTES) {
    return manifestes.find((m) => m.id === id);
}
/** Module propriétaire d'un chemin : le préfixe le plus long l'emporte. */
export function moduleDeRoute(chemin, manifestes = MANIFESTES) {
    const s = segments(chemin);
    let meilleur;
    for (const m of manifestes) {
        for (const r of m.routesApi) {
            const p = segments(r);
            if (p.length > s.length)
                continue;
            if (!p.every((x, i) => x === s[i]))
                continue;
            if (!meilleur || p.length > meilleur.longueur)
                meilleur = { m, longueur: p.length };
        }
    }
    return meilleur?.m;
}
export function dependantsDirects(id, manifestes = MANIFESTES) {
    return manifestes.filter((m) => m.dependances.includes(id));
}
/** Entrées de navigation, avec l'id du module qui les déclare, triées par ordre. */
export function toutesLesEntreesNavigation(manifestes = MANIFESTES) {
    return manifestes
        .flatMap((m) => m.navigation.map((e) => ({ ...e, module: m.id })))
        .sort((a, b) => a.ordre - b.ordre);
}
//# sourceMappingURL=registre.js.map